// FanRatingsHome.jsx

import React, { useState } from "react";
import FanRatingsCategoryGrid from "./FanRatingsCategoryGrid";
import FanRatingsQuestionList from "./FanRatingsQuestionList";
import FanRatingsRatingPage from "./FanRatingsRatingPage";
import FanRatingsMyRatings from "./FanRatingsMyRatings";
import FanRatingsRankings from "./FanRatingsRankings";
import FanRatingsResult from "./FanRatingsResult";
import useFanRatingsHook from "./useFanRatingsHook";

const FanRatingsHome = () => {
  const [screen, setScreen] = useState("home");
  const [mode, setMode] = useState("rate"); // "rate" | "rankings"
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [submittedRatings, setSubmittedRatings] = useState(null);

  const { getMyRatings } = useFanRatingsHook();

  const hasUserAlreadyRated = async (questionId) => {
    try {
      const res = await getMyRatings();
      if (!res?.success || !Array.isArray(res.data)) return false;

      return res.data.some(
        (r) => String(r.questionId) === String(questionId)
      );
    } catch (err) {
      console.error("UserAlreadyRated error:", err);
      return false;
    }
  };

  const handleQuestionSelected = async (question) => {
    setSelectedQuestion(question);

    if (mode === "rankings") {
      // Always allow viewing rankings
      setScreen("rankings");
      return;
    }

    // mode === "rate"
    const alreadyRated = await hasUserAlreadyRated(question._id);

    if (alreadyRated) {
      alert("You have already rated this question. Showing rankings instead.");
      setScreen("rankings");
    } else {
      setScreen("rating");
    }
  };

  return (
    <div className="min-h-screen bg-purple-950 text-gray-50">
      <div className="sticky top-0 z-20 bg-purple-900 border-b border-fuchsia-700">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <h2 className="text-lg md:text-2xl font-bold text-gray-50">
            ⭐ Fan Ratings
          </h2>
          <p className="text-xs md:text-sm text-gray-100 mt-1">
            Rate • Compare • Discover Community Rankings
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3">
        {/* HOME */}
        {screen === "home" && (
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={() => {
                setMode("rate");
                setScreen("category");
              }}
              className="bg-purple-800 hover:bg-purple-700 rounded-xl p-5 border border-fuchsia-600 text-left transition-all duration-200"
            >
              <h3 className="text-base md:text-lg font-bold text-gray-50">
                🎯 Select Category
              </h3>
              <p className="text-xs md:text-sm mt-1 text-gray-100">
                Start a new Fan Rating
              </p>
            </button>

            <button
              onClick={() => setScreen("myratings")}
              className="bg-purple-800 hover:bg-purple-700 rounded-xl p-5 border border-fuchsia-600 text-left transition-all duration-200"
            >
              <h3 className="text-base md:text-lg font-bold text-gray-50">
                ⭐ My Ratings
              </h3>
              <p className="text-xs md:text-sm mt-1 text-gray-100">
                View your submitted ratings
              </p>
            </button>

            <button
              onClick={() => {
                setMode("rankings");
                setScreen("category");
              }}
              className="bg-purple-800 hover:bg-purple-700 rounded-xl p-5 border border-fuchsia-600 text-left transition-all duration-200"
            >
              <h3 className="text-base md:text-lg font-bold text-gray-50">
                🏆 Rankings
              </h3>
              <p className="text-xs md:text-sm mt-1 text-gray-100">
                Community rankings and scores
              </p>
            </button>
          </div>
        )}

        {/* CATEGORY */}
        {screen === "category" && (
          <FanRatingsCategoryGrid
            onBack={() => setScreen("home")}
            onCategorySelected={(category) => {
              setSelectedCategory(category);
              setScreen("questions");
            }}
          />
        )}

        {/* QUESTIONS */}
        {screen === "questions" && (
          <FanRatingsQuestionList
            category={selectedCategory}
            onBack={() => setScreen("category")}
            onQuestionSelected={handleQuestionSelected}
          />
        )}

        {/* RATING */}
        {screen === "rating" && (
          <FanRatingsRatingPage
            question={selectedQuestion}
            onBack={() => setScreen("questions")}
            onSubmit={(payload) => {
              setSubmittedRatings(payload);
              setScreen("result");
            }}
          />
        )}

        {/* RESULT */}
        {screen === "result" && (
          <FanRatingsResult
            question={selectedQuestion}
            payload={submittedRatings}
            onHome={() => setScreen("home")}
            onBack={() => setScreen("home")}
          />
        )}

        {/* MY RATINGS */}
        {screen === "myratings" && (
          <FanRatingsMyRatings onBack={() => setScreen("home")} />
        )}

        {/* RANKINGS */}
        {screen === "rankings" && (
          <FanRatingsRankings
            question={selectedQuestion}
            onBack={() => setScreen("questions")}
          />
        )}
      </div>
    </div>
  );
};

export default FanRatingsHome;